import type { AlertListQuery } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import { AlertStatusCell } from './AlertStatusCell';
import { AlertTruckCell } from './AlertTruckCell';
import { ReassignAlertPopover, type ReassignAlert } from './ReassignAlertPopover';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { AlertView } from '../alerts.api';
import { alertAge } from '../policies/alert-age';
import { ALERT_SEVERITY_LABELS, ALERT_STATUS_LABELS, ALERT_TYPE_LABELS, SEVERITY_TAG_COLOURS, toColumnFilters } from '../policies/alert-labels';

interface RaisedAlertColumnsOptions {
  list: ListQueryControls<AlertListQuery>;
  /** For the Age column, which keeps counting while the screen is open. */
  now: Date;
  canRespond: boolean;
  canAssign: boolean;
  answeringId: string | null;
  onAnswer: (alert: AlertView, action: 'acknowledge' | 'resolve') => void;
  onReassign: ReassignAlert;
}

export function raisedAlertColumns({
  list,
  now,
  canRespond,
  canAssign,
  answeringId,
  onAnswer,
  onReassign,
}: RaisedAlertColumnsOptions): TableColumnsType<AlertView> {
  const columns: TableColumnsType<AlertView> = [
    { title: 'Truck', key: 'truck', render: (_, alert) => <AlertTruckCell alert={alert} /> },
    { title: 'What happened', dataIndex: 'summary', key: 'summary' },
    {
      title: 'Severity',
      key: 'severity',
      sorter: true,
      sortOrder: list.sortOrderFor('severity'),
      filters: toColumnFilters(ALERT_SEVERITY_LABELS),
      filteredValue: list.filteredValueFor('severity'),
      render: (_, alert) => <Tag color={SEVERITY_TAG_COLOURS[alert.severity]}>{ALERT_SEVERITY_LABELS[alert.severity]}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      filters: toColumnFilters(ALERT_STATUS_LABELS),
      filteredValue: list.filteredValueFor('status'),
      render: (_, alert) => <AlertStatusCell alert={alert} />,
    },
    { title: 'Owner', key: 'owner', render: (_, alert) => alert.ownerName ?? <span className="text-muted">Unassigned</span> },
    { title: 'Age', key: 'age', render: (_, alert) => alertAge(alert, now) },
    {
      title: 'Raised',
      key: 'raisedAt',
      sorter: true,
      sortOrder: list.sortOrderFor('raisedAt'),
      render: (_, alert) => new Date(alert.raisedAt).toLocaleString(),
    },
    {
      title: 'Rule',
      key: 'type',
      render: (_, alert) => `${alert.ruleName} (${ALERT_TYPE_LABELS[alert.type]})`,
      filters: toColumnFilters(ALERT_TYPE_LABELS),
      filteredValue: list.filteredValueFor('type'),
    },
  ];
  if (!canRespond && !canAssign) return columns;
  return [...columns, actionsColumn({ canRespond, canAssign, answeringId, onAnswer, onReassign })];
}

/** What a person may do to an alert, by permission (UX gating only; the server decides) and by where the alert is in its life. */
function actionsColumn({
  canRespond,
  canAssign,
  answeringId,
  onAnswer,
  onReassign,
}: Omit<RaisedAlertColumnsOptions, 'list' | 'now'>): TableColumnsType<AlertView>[number] {
  return {
    title: '',
    key: 'actions',
    render: (_, alert) => (
      <div className="flex gap-2">
        {canRespond && alert.status === 'open' && (
          <Button size="small" loading={answeringId === alert.id} onClick={() => onAnswer(alert, 'acknowledge')}>
            Acknowledge
          </Button>
        )}
        {canRespond && alert.status !== 'resolved' && (
          <Button size="small" loading={answeringId === alert.id} onClick={() => onAnswer(alert, 'resolve')}>
            Resolve
          </Button>
        )}
        {canAssign && alert.status !== 'resolved' && <ReassignAlertPopover alert={alert} onReassign={onReassign} />}
      </div>
    ),
  };
}
